import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Copy, Trash2, ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} image(s) uploaded`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const fullUrl = (item) => `${BACKEND_URL}${item.url}`;

  const copy = (item) => {
    navigator.clipboard.writeText(fullUrl(item));
    toast.success("Image URL copied");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/media/${id}`);
      setItems((it) => it.filter((m) => m.id !== id));
      toast.success("Image deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-media">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">Media Library</h1>
          <p className="mt-1 text-sm text-slate-400">{items.length} image(s). Upload and copy URLs to use in blog & portfolio.</p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-vantage px-4 py-2 text-sm font-semibold text-white hover:bg-vantage-hover" data-testid="media-upload-label">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload images
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="media-upload-input" />
        </label>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-vantage" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 bg-ink-900 py-20 text-center">
          <ImageIcon className="h-10 w-10 text-slate-600" />
          <p className="mt-4 text-slate-400">No images yet. Upload your first image.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((m) => (
            <div key={m.id} className="group overflow-hidden rounded-2xl border border-white/10 bg-ink-900" data-testid={`media-${m.id}`}>
              <div className="aspect-square overflow-hidden bg-ink-800">
                <img src={fullUrl(m)} alt={m.filename} loading="lazy" className="h-full w-full object-cover" />
              </div>
              <div className="p-3">
                <p className="truncate text-xs text-slate-400" title={m.filename}>{m.filename}</p>
                <div className="mt-2 flex gap-2">
                  <button onClick={() => copy(m)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-white/5 py-1.5 text-xs text-slate-300 hover:bg-white/10" data-testid={`media-copy-${m.id}`}>
                    <Copy className="h-3.5 w-3.5" /> Copy URL
                  </button>
                  <button onClick={() => remove(m.id)} className="rounded-lg bg-white/5 p-1.5 text-slate-400 hover:bg-red-500/10 hover:text-red-400" data-testid={`media-delete-${m.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
